import { AsyncPipe } from "@angular/common";
import { Component, DestroyRef, effect, inject, input, OnInit } from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { FormBuilder, FormGroup, ReactiveFormsModule } from "@angular/forms";
import { UiKitModule } from "@app/ui-kit.module";
import { AirPath } from "@domain/project/floorplan/air-path";
import { FloorplanItem } from "@domain/project/floorplan/floorplan-item";
import { MeasurementLine } from "@domain/project/floorplan/measurement-line";
import { CableRoute } from "@domain/project/floorplan/wiring/cable-route";
import { Pipeline } from "@domain/project/floorplan/wiring/pipeline";
import { ProtectiveTube } from "@domain/project/floorplan/wiring/protective-tube";
import { Wire } from "@domain/project/floorplan/wiring/wire";
import { ProjectService } from "@domain/project/project.service";
import { UserService } from "@domain/user/user.service";
import { FormDirective, FormFieldComponent, FormFieldControlDirective } from "@odx/angular/components/form-field";
import { TooltipDirective } from "@odx/angular/components/tooltip";
import { FloorplanService } from "@project/floorplanner/floorplan.service";
import { filter, map, Observable, Subject, takeUntil, withLatestFrom } from "rxjs";

@Component({
  selector: "app-reference-measurement-item-length",
  imports: [
    FormDirective,
    FormFieldComponent,
    ReactiveFormsModule,
    FormFieldControlDirective,
    AsyncPipe,
    TooltipDirective,
    UiKitModule,
  ],
  templateUrl: "./reference-measurement-item-length.component.html",
  styleUrl: "./reference-measurement-item-length.component.scss",
  standalone: true,
})
export class ReferenceMeasurementItemLengthComponent implements OnInit {
  protected isReferenceMeasureSet!: boolean;

  private readonly formBuilder = inject(FormBuilder);
  private readonly userService = inject(UserService);
  private readonly projectService = inject(ProjectService);
  private readonly floorplanService = inject(FloorplanService);
  private readonly destroyRef = inject(DestroyRef);

  readonly floorplanItem = input.required<FloorplanItem>();

  protected conduit!: AirPath | CableRoute | Pipeline | ProtectiveTube | Wire | MeasurementLine;
  protected conduitLengthForm?: FormGroup;
  protected isReadOnlyUser$: Observable<boolean>;
  protected hasWritePermission$: Observable<boolean | undefined>;
  protected isReferenceMeasureSet$!: Observable<boolean>;

  protected readonly tooltipTextNoReferenceMeasure: string = $localize`:@@floorplan.referenceMeasure.noReferenceMeasureIsSet:Ein Referenzmaß ist für Änderungen erforderlich`;

  constructor() {
    this.isReadOnlyUser$ = this.userService.user$.pipe(map((user) => user.readOnlyAccess));
    this.hasWritePermission$ = this.projectService.hasUserWritePermission$.pipe(
      filter((val): val is boolean => val !== undefined),
    );
    effect(() => {
      const item = this.floorplanItem();
      if (item instanceof AirPath) this.conduit = item as AirPath;
      else if (item instanceof CableRoute) this.conduit = item as CableRoute;
      else if (item instanceof Pipeline) this.conduit = item as Pipeline;
      else if (item instanceof ProtectiveTube) this.conduit = item as ProtectiveTube;
      else if (item instanceof Wire) this.conduit = item as Wire;
      else if (item instanceof MeasurementLine) this.conduit = item as MeasurementLine;
      const cleanUp$ = new Subject<void>();
      this.setupConduitForm(cleanUp$);
      return () => {
        cleanUp$.next();
        cleanUp$.complete();
      };
    });
  }

  ngOnInit(): void {
    this.isReferenceMeasureSet$ = this.floorplanService.isReferenceMeasureSet$;
  }

  private setupConduitForm(destroy$: Subject<void>) {
    if (!this.conduitLengthForm) {
      this.initItemLengthForm();
    } else {
      this.conduitLengthForm.reset({ referenceLength: this.conduit.referenceLength }, { emitEvent: false });
    }

    this.conduitLengthForm!.valueChanges.pipe(
      withLatestFrom(this.isReferenceMeasureSet$),
      takeUntil(destroy$),
      takeUntilDestroyed(this.destroyRef),
    ).subscribe(([values, isReferenceMeasureSet]) => {
      if (!isReferenceMeasureSet) {
        return;
      }
      if (this.hasReferenceLengthChanged(values.referenceLength)) {
        if (values.referenceLength) this.conduit.updateLength(values.referenceLength);
      }
    });

    this.conduit.lengthChanged.pipe(takeUntil(destroy$), takeUntilDestroyed(this.destroyRef)).subscribe(({ length }) => {
      this.conduit.updateReferenceLength(length);
      this.patchLengthValues();
    });

    this.conduit.referenceLengthChanged
      .pipe(takeUntil(destroy$), takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.patchLengthValues());
  }

  private initItemLengthForm() {
    this.conduit.lengthChanged = new Subject();
    this.conduitLengthForm = this.formBuilder.group({
      referenceLength: [this.conduit.referenceLength],
    });
  }

  private patchLengthValues() {
    this.conduitLengthForm!.patchValue({ referenceLength: this.conduit.referenceLength }, { emitEvent: false });
  }

  private hasReferenceLengthChanged(referenceLength: number | undefined) {
    return !(referenceLength === this.conduit.referenceLength);
  }
}
